import { useEffect, useRef } from "react";
import { GmailAccount } from "../types";

interface UseGmailAccountUrlSyncArgs {
  accounts: GmailAccount[];
  activeAccount: string | null;
  /**
   * The URL's current `?account=`, from `useEmailPageUrlSync`. Left
   * `undefined` by callers with no URL of their own to stay in sync with
   * (`EmailSettings`'s standalone `useGmail()` call) — this hook simply
   * never has anything to do in that case.
   */
  urlAccount?: string | null;
  setActiveAccount: (gmailEmail: string) => void;
  /**
   * Writes the resolved account into the URL when it doesn't name one yet
   * — `useEmailPageUrlSync`'s `onAccountResolved`. Left `undefined` by
   * callers with no URL of their own, same as `urlAccount`.
   */
  onAccountResolved?: (gmailEmail: string) => void;
}

/**
 * The browser back/forward buttons change `urlAccount` without ever
 * calling `setActiveAccount` — this switches the mailbox to match when
 * that happens, the same way `useEmailPageSelection` follows the URL's
 * restored sender/thread.
 *
 * Reacts only to `urlAccount` actually *transitioning* (tracked via
 * `prevUrlAccountRef`), not to it merely differing from `activeAccount` —
 * a switch driven by the UI (clicking an account tab) sets `activeAccount`
 * synchronously but the URL write lands a render or two later, and reading
 * `activeAccount` directly in that window would race: it would see the
 * stale `urlAccount` as "wrong" and switch straight back. Comparing
 * against a ref of the *latest* `activeAccount` (not a dependency, so it
 * can't itself retrigger this effect) at the moment `urlAccount` really
 * does change avoids both that race and, once the URL catches up, a
 * redundant reset of the mailbox that's already showing.
 *
 * A `urlAccount` naming no account (or one that's gone, e.g. disconnected
 * since) falls back to the same default `useGmail`'s own connection load
 * would pick — the first connected account.
 */
export function useGmailAccountUrlSync({
  accounts,
  activeAccount,
  urlAccount,
  setActiveAccount,
  onAccountResolved,
}: UseGmailAccountUrlSyncArgs) {
  const prevUrlAccountRef = useRef(urlAccount);
  const activeAccountRef = useRef(activeAccount);
  activeAccountRef.current = activeAccount;

  useEffect(() => {
    const prev = prevUrlAccountRef.current;
    prevUrlAccountRef.current = urlAccount;
    if (urlAccount === prev || accounts.length === 0) return;

    const target =
      urlAccount && accounts.some((a) => a.gmail_email === urlAccount)
        ? urlAccount
        : accounts[0].gmail_email;
    if (target !== activeAccountRef.current) {
      setActiveAccount(target);
    }
  }, [urlAccount, accounts, setActiveAccount]);

  // Landing on /emails with no `?account=` (a fresh nav from elsewhere in
  // the app, or a bare deep link) leaves the URL silent about which mailbox
  // is showing until the user explicitly switches one — a refresh or a
  // copied link would then fall back to whichever account loads first
  // rather than the one actually on screen. Once useGmail resolves its
  // default, name it in the URL too. `urlAccount === undefined` opts out
  // (no URL of this hook's own to write into), same as the effect above.
  useEffect(() => {
    if (urlAccount != null || !activeAccount) return;
    onAccountResolved?.(activeAccount);
  }, [urlAccount, activeAccount, onAccountResolved]);
}
